import i18n from "../../i18n";
import resourcesAr from "../../data/resources.ar.json";
import resourcesEn from "../../data/resources.en.json";
import MarkdownResourcePage from "./MarkdownResourcePage";

const normalizeLanguage = (language) =>
  String(language || "")
    .toLowerCase()
    .startsWith("ar")
    ? "ar"
    : "en";

const getDatasetSections = (dataset) =>
  Array.isArray(dataset?.item) ? dataset.item : [];

const getSectionItems = (section) =>
  Array.isArray(section?.item) ? section.item : [];

const normalizeKeywords = (keywords) =>
  Array.isArray(keywords) ? keywords : [];

const normalizeContent = (value) => (typeof value === "string" ? value : "");

const toPascalCase = (value) =>
  value
    .split("-")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join("");

export const getManifestPageLocale = (
  page,
  language = i18n.resolvedLanguage || i18n.language,
) => {
  if (!page?.locales) return null;
  return page.locales[normalizeLanguage(language)] || page.locales.en || null;
};

export const getManifestSectionLocale = (
  section,
  language = i18n.resolvedLanguage || i18n.language,
) => {
  if (!section?.locales) return null;
  return (
    section.locales[normalizeLanguage(language)] || section.locales.en || null
  );
};

const createManifestPageComponent = (page) => {
  const Component = () => <MarkdownResourcePage page={page} />;
  Component.displayName = `${toPascalCase(page.slug)}MarkdownResourcePage`;
  return Component;
};

const toPageLocale = (page) => ({
  title: page?.name || "",
  description: page?.summary || "",
  keywords: normalizeKeywords(page?.keywords),
  content: normalizeContent(page?.content),
});

const buildManifest = (datasetsByLanguage, defaultLanguage = "en") => {
  const defaultSections = getDatasetSections(
    datasetsByLanguage[defaultLanguage],
  );

  const sectionsByLanguage = Object.fromEntries(
    Object.entries(datasetsByLanguage).map(([language, dataset]) => [
      language,
      new Map(
        getDatasetSections(dataset).map((section) => [section.key, section]),
      ),
    ]),
  );

  const sections = defaultSections.map((defaultSection, sectionOrder) => ({
    key: defaultSection.key,
    order: sectionOrder,
    labelKey: null,
    locales: Object.fromEntries(
      Object.keys(datasetsByLanguage)
        .map((language) => {
          const localizedSection = sectionsByLanguage[language].get(
            defaultSection.key,
          );

          if (!localizedSection) {
            return null;
          }

          return [
            language,
            { name: localizedSection.name || defaultSection.key },
          ];
        })
        .filter(Boolean),
    ),
  }));

  const pages = [];

  sections.forEach((section) => {
    const defaultSection = sectionsByLanguage[defaultLanguage].get(section.key);

    if (!defaultSection) {
      return;
    }

    const pagesByLanguage = Object.fromEntries(
      Object.entries(datasetsByLanguage).map(([language]) => {
        const localizedSection = sectionsByLanguage[language].get(section.key);

        return [
          language,
          new Map(
            getSectionItems(localizedSection).map((page) => [page.slug, page]),
          ),
        ];
      }),
    );

    getSectionItems(defaultSection).forEach((defaultPage, pageOrder) => {
      const locales = Object.fromEntries(
        Object.keys(datasetsByLanguage)
          .map((language) => {
            const localizedPage = pagesByLanguage[language].get(
              defaultPage.slug,
            );

            if (!localizedPage) {
              return null;
            }

            return [language, toPageLocale(localizedPage)];
          })
          .filter(Boolean),
      );

      const manifestPage = {
        slug: defaultPage.slug,
        title:
          locales[defaultLanguage]?.title ||
          defaultPage.name ||
          defaultPage.slug,
        description:
          locales[defaultLanguage]?.description || defaultPage.summary || "",
        keywords:
          locales[defaultLanguage]?.keywords ||
          normalizeKeywords(defaultPage.keywords),
        sectionKey: section.key,
        sectionOrder: section.order,
        pageOrder,
        sectionLocales: section.locales,
        locales,
        contentType: "markdown",
      };

      manifestPage.component = createManifestPageComponent(manifestPage);
      pages.push(manifestPage);
    });
  });

  return { sections, pages };
};

const { sections, pages } = buildManifest(
  {
    en: resourcesEn,
    ar: resourcesAr,
  },
  "en",
);

export const manifestSections = sections;
export const manifestResourcePages = pages;
